/**
 * SEO Image Alt Text Generator
 * 
 * Provides localized, keyword-rich alt texts for all product images.
 * These alt texts are optimized for Google Image Search traffic.
 * 
 * IMPORTANT: Customer photos must NEVER get SEO alt texts.
 */

type Language = 'en' | 'de' | 'es' | 'fr' | 'it' | 'pt' | 'ru' | 'sv' | 'zh';

// Product name translations for alt tags
const productNames: Record<string, Record<Language, string>> = {
  'drivers-license': {
    en: 'International Drivers License',
    de: 'Internationaler Führerschein',
    es: 'Licencia de Conducir Internacional',
    fr: 'Permis de Conduire International',
    it: 'Patente di Guida Internazionale',
    pt: 'Carteira de Motorista Internacional',
    ru: 'Международные водительские права',
    sv: 'Internationellt Körkort',
    zh: '国际驾照',
  },
  'state-id': {
    en: 'State ID Card',
    de: 'Staatsausweis',
    es: 'Tarjeta de Identificación Estatal',
    fr: "Carte d'Identité d'État",
    it: 'Carta d\'Identità Statale',
    pt: 'Carteira de Identidade Estadual',
    ru: 'Удостоверение личности штата',
    sv: 'Statligt ID-kort',
    zh: '州身份证',
  },
  'national-id': {
    en: 'National ID Card',
    de: 'Personalausweis',
    es: 'Documento Nacional de Identidad',
    fr: "Carte Nationale d'Identité",
    it: 'Carta d\'Identità Nazionale',
    pt: 'Documento de Identidade Nacional',
    ru: 'Национальное удостоверение личности',
    sv: 'Nationellt ID-kort',
    zh: '国民身份证',
  },
  'photo-id': {
    en: 'Photo ID Card',
    de: 'Lichtbildausweis',
    es: 'Tarjeta de Identificación con Foto',
    fr: "Carte d'Identité avec Photo",
    it: 'Tessera di Riconoscimento con Foto',
    pt: 'Carteira de Identidade com Foto',
    ru: 'Удостоверение личности с фото',
    sv: 'Foto-ID Kort',
    zh: '照片ID卡',
  },
  'student-id': {
    en: 'Student ID Card',
    de: 'Studentenausweis',
    es: 'Carnet de Estudiante',
    fr: 'Carte Étudiant',
    it: 'Tessera Studentesca',
    pt: 'Carteira de Estudante',
    ru: 'Студенческий билет',
    sv: 'Studentlegitimation',
    zh: '学生证',
  },
  'press-card': {
    en: 'Press ID Card',
    de: 'Presseausweis',
    es: 'Credencial de Prensa',
    fr: 'Carte de Presse',
    it: 'Tessera Stampa',
    pt: 'Credencial de Imprensa',
    ru: 'Пресс-карта',
    sv: 'Presslegitimation',
    zh: '记者证',
  },
  'fishing-license': {
    en: 'Fishing License',
    de: 'Angelschein',
    es: 'Licencia de Pesca',
    fr: 'Permis de Pêche',
    it: 'Licenza di Pesca',
    pt: 'Licença de Pesca',
    ru: 'Рыболовная лицензия',
    sv: 'Fiskekort',
    zh: '钓鱼许可证',
  },
  'boater-license': {
    en: 'Boater License',
    de: 'Bootsführerschein',
    es: 'Licencia de Navegación',
    fr: 'Permis Bateau',
    it: 'Patente Nautica',
    pt: 'Habilitação Náutica',
    ru: 'Удостоверение судоводителя',
    sv: 'Båtkörkort',
    zh: '船舶驾照',
  },
};

const productDescriptions: Record<string, Record<Language, string>> = {
  'drivers-license': {
    en: "Premium international driver's license with holographic security features and multi-language support.",
    de: 'Premium internationaler Führerschein mit holografischen Sicherheitsmerkmalen und mehrsprachiger Unterstützung.',
    es: 'Licencia internacional premium con características de seguridad holográficas y soporte multilingüe.',
    fr: 'Permis de conduire international premium avec éléments de sécurité holographiques et prise en charge multilingue.',
    it: 'Patente internazionale premium con elementi di sicurezza olografici e supporto multilingue.',
    pt: 'Carteira internacional premium com recursos de segurança holográficos e suporte multilíngue.',
    ru: 'Премиальные международные водительские права с голографической защитой и поддержкой нескольких языков.',
    sv: 'Premium internationellt körkort med holografiska säkerhetsfunktioner och flerspråkigt stöd.',
    zh: '带全息安全特征和多语言支持的优质国际驾照模板。',
  },
  'state-id': {
    en: 'Official-looking state identification card with modern security features.',
    de: 'Täuschend echt wirkender Staatsausweis mit modernen Sicherheitsmerkmalen.',
    es: 'Tarjeta de identificación estatal de aspecto oficial con funciones de seguridad modernas.',
    fr: "Carte d'identité d'État d'apparence officielle avec des éléments de sécurité modernes.",
    it: 'Carta d’identità statale dall’aspetto ufficiale con moderne caratteristiche di sicurezza.',
    pt: 'Cartão de identificação estadual com aparência oficial e recursos modernos de segurança.',
    ru: 'Удостоверение личности штата с официальным внешним видом и современными функциями защиты.',
    sv: 'Officiellt utseende statligt ID-kort med moderna säkerhetsfunktioner.',
    zh: '具备现代安全特征的逼真州身份证模板。',
  },
  'national-id': {
    en: 'Professional national identification card with advanced security features.',
    de: 'Professioneller Personalausweis mit fortschrittlichen Sicherheitsmerkmalen.',
    es: 'Documento nacional de identidad profesional con características de seguridad avanzadas.',
    fr: "Carte nationale d'identité professionnelle avec des éléments de sécurité avancés.",
    it: 'Carta d’identità nazionale professionale con funzionalità di sicurezza avanzate.',
    pt: 'Documento nacional de identificação profissional com recursos avançados de segurança.',
    ru: 'Профессиональное национальное удостоверение личности с расширенными функциями безопасности.',
    sv: 'Professionellt nationellt ID-kort med avancerade säkerhetsfunktioner.',
    zh: '配备高级安全细节的专业国民身份证模板。',
  },
  'photo-id': {
    en: 'Versatile photo ID card perfect for various identification needs.',
    de: 'Vielseitiger Lichtbildausweis, ideal für verschiedenste Identifikationszwecke.',
    es: 'Tarjeta de identificación con foto versátil, ideal para diversas necesidades de identificación.',
    fr: "Carte d'identité photo polyvalente, idéale pour différents besoins d'identification.",
    it: 'Tessera con foto versatile, perfetta per diverse esigenze di identificazione.',
    pt: 'Cartão de identificação com foto versátil, perfeito para várias necessidades de identificação.',
    ru: 'Универсальное удостоверение личности с фото для самых разных задач идентификации.',
    sv: 'Mångsidigt foto-ID-kort som passar olika identifieringsbehov.',
    zh: '适用于多种识别用途的多功能照片ID卡模板。',
  },
  'student-id': {
    en: 'Professional student ID card with institution branding and photo.',
    de: 'Professioneller Studentenausweis mit Schulbranding und Foto.',
    es: 'Carnet estudiantil profesional con marca institucional y foto.',
    fr: 'Carte étudiant professionnelle avec identité visuelle de l’établissement et photo.',
    it: 'Tessera studentesca professionale con branding dell’istituto e foto.',
    pt: 'Cartão estudantil profissional com identidade visual da instituição e foto.',
    ru: 'Профессиональный студенческий билет с брендингом учебного заведения и фотографией.',
    sv: 'Professionellt student-ID-kort med skolprofilering och foto.',
    zh: '带院校品牌和照片区域的专业学生证模板。',
  },
  'press-card': {
    en: 'Professional press and media identification card.',
    de: 'Professioneller Presse- und Medienausweis.',
    es: 'Credencial profesional de prensa y medios.',
    fr: 'Carte professionnelle de presse et de média.',
    it: 'Tessera professionale per stampa e media.',
    pt: 'Cartão profissional de imprensa e mídia.',
    ru: 'Профессиональная пресс-карта для СМИ и журналистов.',
    sv: 'Professionellt press- och media-ID-kort.',
    zh: '适用于媒体和新闻工作者的专业记者证模板。',
  },
  'fishing-license': {
    en: 'Recreational fishing license with waterproof lamination.',
    de: 'Freizeit-Angelschein mit wasserfester Laminierung.',
    es: 'Licencia de pesca recreativa con laminado impermeable.',
    fr: 'Permis de pêche loisir avec laminage imperméable.',
    it: 'Licenza di pesca ricreativa con laminazione impermeabile.',
    pt: 'Licença de pesca recreativa com laminação impermeável.',
    ru: 'Любительская рыболовная лицензия с водостойкой ламинацией.',
    sv: 'Fiskekort för fritidsbruk med vattentålig laminering.',
    zh: '采用防水覆膜的休闲钓鱼许可证模板。',
  },
  'boater-license': {
    en: 'Recreational boating license with marine-grade materials.',
    de: 'Freizeit-Bootsführerschein mit maritimen Premiummaterialien.',
    es: 'Licencia náutica recreativa con materiales de calidad marina.',
    fr: 'Permis bateau loisir avec matériaux de qualité marine.',
    it: 'Patente nautica ricreativa con materiali di qualità marina.',
    pt: 'Habilitação náutica recreativa com materiais de padrão marítimo.',
    ru: 'Любительское удостоверение судоводителя из морских износостойких материалов.',
    sv: 'Båtkörkort för fritidsbruk med marina kvalitetsmaterial.',
    zh: '采用耐候材料制作的船舶驾照模板。',
  },
};

const genericProductDescriptions: Record<Language, string> = {
  en: 'Premium quality card with realistic security features.',
  de: 'Premium-Qualität mit realistischen Sicherheitsmerkmalen.',
  es: 'Calidad premium con características de seguridad realistas.',
  fr: 'Qualité premium avec des éléments de sécurité réalistes.',
  it: 'Qualità premium con caratteristiche di sicurezza realistiche.',
  pt: 'Qualidade premium com recursos de segurança realistas.',
  ru: 'Премиальное качество с реалистичными защитными элементами.',
  sv: 'Premiumkvalitet med realistiska säkerhetsfunktioner.',
  zh: '优质品质，带有逼真的安全特征。',
};

// Alt text templates per context
const altTemplates: Record<string, Record<Language, string>> = {
  front: {
    en: '{product} – Front Side with Hologram Security Features',
    de: '{product} – Vorderseite mit Hologramm-Sicherheitsmerkmalen',
    es: '{product} – Lado Frontal con Holograma de Seguridad',
    fr: '{product} – Recto avec Hologramme de Sécurité',
    it: '{product} – Lato Frontale con Ologramma di Sicurezza',
    pt: '{product} – Frente com Holograma de Segurança',
    ru: '{product} – Лицевая сторона с голограммой безопасности',
    sv: '{product} – Framsida med Hologram Säkerhetsfunktioner',
    zh: '{product} – 正面全息安全功能',
  },
  back: {
    en: '{product} – Back Side with Scannable Barcode',
    de: '{product} – Rückseite mit scannbarem Barcode',
    es: '{product} – Lado Posterior con Código de Barras Escaneable',
    fr: '{product} – Verso avec Code-Barres Scannable',
    it: '{product} – Retro con Codice a Barre Scansionabile',
    pt: '{product} – Verso com Código de Barras Escaneável',
    ru: '{product} – Обратная сторона со сканируемым штрих-кодом',
    sv: '{product} – Baksida med Skanningsbar Streckkod',
    zh: '{product} – 背面可扫描条形码',
  },
  showcase: {
    en: '{product} – Premium Quality Fake ID Card Example',
    de: '{product} – Hochwertiger Fake Ausweis Beispiel',
    es: '{product} – Ejemplo de Identificación Falsa Premium',
    fr: '{product} – Exemple de Fausse Carte d\'Identité Premium',
    it: '{product} – Esempio di Documento Falso Premium',
    pt: '{product} – Exemplo de Identidade Falsa Premium',
    ru: '{product} – Пример поддельного удостоверения премиум-качества',
    sv: '{product} – Premium Kvalitet Falskt ID-kort Exempel',
    zh: '{product} – 优质仿真证件示例',
  },
  animation: {
    en: '{product} with 3D Hologram Animation – Both Sides',
    de: '{product} mit 3D-Hologramm-Animation – Beide Seiten',
    es: '{product} con Animación de Holograma 3D – Ambos Lados',
    fr: '{product} avec Animation Hologramme 3D – Recto-Verso',
    it: '{product} con Animazione Ologramma 3D – Entrambi i Lati',
    pt: '{product} com Animação de Holograma 3D – Ambos os Lados',
    ru: '{product} с 3D-голограммой – Обе стороны',
    sv: '{product} med 3D Hologram Animation – Båda Sidor',
    zh: '{product} 3D全息动画 – 双面',
  },
  gallery: {
    en: '{product} – Realistic Design Detail View {index}',
    de: '{product} – Realistisches Design Detailansicht {index}',
    es: '{product} – Vista Detallada del Diseño Realista {index}',
    fr: '{product} – Vue Détaillée du Design Réaliste {index}',
    it: '{product} – Vista Dettagliata del Design Realistico {index}',
    pt: '{product} – Visão Detalhada do Design Realista {index}',
    ru: '{product} – Детальный вид реалистичного дизайна {index}',
    sv: '{product} – Realistisk Design Detaljvy {index}',
    zh: '{product} – 逼真设计详细视图 {index}',
  },
  hero: {
    en: 'Buy {product} Online – Custom Fake ID with Hologram',
    de: '{product} Online Kaufen – Fake Ausweis mit Hologramm',
    es: 'Comprar {product} Online – ID Falso con Holograma',
    fr: 'Acheter {product} en Ligne – Fausse ID avec Hologramme',
    it: 'Acquista {product} Online – Documento Falso con Ologramma',
    pt: 'Comprar {product} Online – ID Falso com Holograma',
    ru: 'Купить {product} Онлайн – Поддельное удостоверение с голограммой',
    sv: 'Köp {product} Online – Falskt ID med Hologram',
    zh: '在线购买{product} – 带全息图的仿真证件',
  },
  hologram: {
    en: 'Hologram security feature on fake ID card – both-sided holographic overlay',
    de: 'Hologramm-Sicherheitsmerkmal auf Fake Ausweis – beidseitige holografische Beschichtung',
    es: 'Función de seguridad holográfica en ID falso – holograma en ambos lados',
    fr: 'Hologramme de sécurité sur fausse carte d\'identité – revêtement holographique recto-verso',
    it: 'Funzione di sicurezza olografica su documento falso – rivestimento olografico fronte-retro',
    pt: 'Recurso de segurança holográfico em ID falso – sobreposição holográfica em ambos os lados',
    ru: 'Голографическая защита на поддельном удостоверении – двусторонняя голограмма',
    sv: 'Hologram säkerhetsfunktion på falskt ID-kort – dubbelsidig holografisk beläggning',
    zh: '仿真证件上的全息安全功能 – 双面全息覆盖',
  },
  barcode: {
    en: 'Scannable PDF417 barcode on fake ID – scan with smartphone',
    de: 'Scannbarer PDF417 Barcode auf Fake Ausweis – mit Smartphone scannen',
    es: 'Código de barras PDF417 escaneable en ID falso – escanear con smartphone',
    fr: 'Code-barres PDF417 scannable sur fausse ID – scanner avec smartphone',
    it: 'Codice a barre PDF417 scansionabile su documento falso – scansiona con smartphone',
    pt: 'Código de barras PDF417 escaneável em ID falso – escaneie com smartphone',
    ru: 'Сканируемый штрих-код PDF417 на поддельном удостоверении – сканирование смартфоном',
    sv: 'Skanningsbar PDF417 streckkod på falskt ID – skanna med smartphone',
    zh: '仿真证件上的可扫描PDF417条形码 – 用智能手机扫描',
  },
  delivery: {
    en: 'Discreet fake ID shipping in plain envelope – branding free delivery',
    de: 'Diskreter Fake Ausweis Versand im neutralen Umschlag – markenfreie Lieferung',
    es: 'Envío discreto de ID falso en sobre neutro – entrega sin marca',
    fr: 'Livraison discrète de fausse ID en enveloppe neutre – sans marque',
    it: 'Spedizione discreta di documento falso in busta neutra – consegna senza marchio',
    pt: 'Envio discreto de ID falso em envelope neutro – entrega sem marca',
    ru: 'Дискретная доставка поддельного удостоверения в обычном конверте',
    sv: 'Diskret leverans av falskt ID i neutralt kuvert – varumärkesfri leverans',
    zh: '仿真证件低调配送普通信封 – 无品牌标识配送',
  },
};

/**
 * Get the localized product name for a template slug
 */
export function getLocalizedProductName(slug: string, language: Language): string {
  return productNames[slug]?.[language] || productNames[slug]?.en || slug;
}

/**
 * Get the localized short product description for cards in grids/carousels
 */
export function getLocalizedProductDescription(slug: string, language: Language): string {
  return productDescriptions[slug]?.[language]
    || productDescriptions[slug]?.en
    || genericProductDescriptions[language]
    || genericProductDescriptions.en;
}

/**
 * Get an SEO-optimized alt text for a product image
 */
export function getProductImageAlt(
  slug: string,
  context: 'front' | 'back' | 'showcase' | 'animation' | 'gallery' | 'hero',
  language: Language,
  galleryIndex?: number
): string {
  const productName = getLocalizedProductName(slug, language);
  const template = altTemplates[context]?.[language] || altTemplates[context]?.en || '{product}';
  
  return template
    .replace('{product}', productName)
    .replace('{index}', galleryIndex !== undefined ? String(galleryIndex + 1) : '');
}

/**
 * Get alt text for feature images (hologram, barcode, delivery)
 */
export function getFeatureImageAlt(
  feature: 'hologram' | 'barcode' | 'delivery',
  language: Language
): string {
  return altTemplates[feature]?.[language] || altTemplates[feature]?.en || feature;
}

/**
 * Derive a template slug from a product name
 * e.g. "International Driver's License" → "drivers-license"
 */
export function deriveSlugFromName(name: string): string {
  const nameToSlug: Record<string, string> = {
    "international driver's license": 'drivers-license',
    "state id card": 'state-id',
    "national id card": 'national-id',
    "photo id card": 'photo-id',
    "student id card": 'student-id',
    "press id card": 'press-card',
    "fishing license": 'fishing-license',
    "boater license": 'boater-license',
  };
  
  return nameToSlug[name.toLowerCase()] || name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}
